<script setup lang="ts">
import { RouterLinkProps } from "vue-router";

type Props = {
  title: string;
  itemTitle?: string;
  to?: RouterLinkProps["to"];
  href?: string;
  hideSm?: boolean;
};
const props = withDefaults(defineProps<Props>(), {
  itemTitle: undefined,
  to: undefined,
  href: undefined,
  hideSm: false,
});

const isBreadcrumb = computed(() => !!props.itemTitle);
const hasLongItemTitle = computed(
  () => !!props.itemTitle && props.itemTitle.length > 37
);

const component = computed(() => {
  if (props.href) {
    return "a";
  } else {
    return "router-link";
  }
});

const toOrHref = computed(() => {
  if (props.href) {
    return { href: props.href };
  } else if (props.to) {
    return { to: props.to };
  }
  return false;
});
</script>
<template>
  <div
    class="tools-navbar__title"
    data-testid="page-title"
    :class="{ 'd-none d-sm-block': hideSm }"
  >
    <component
      :is="component"
      v-if="toOrHref && isBreadcrumb"
      class="tools-navbar__link"
      data-testid="link-back"
      v-bind="{ ...$attrs, ...toOrHref }"
      :aria-label="title"
    >
      <BaseIcon
        icon-name="arrow-left"
        class="i-lg mt-0 d-sm-none me-3"
        color="dark"
        size="md"
      />
      <span class="d-none d-sm-block ms-2">{{ title }}</span>
    </component>
    <span v-else class="ms-2">{{ title }}</span>

    <span v-if="isBreadcrumb" class="d-none d-sm-block mx-2">/</span>

    <span
      v-if="itemTitle"
      v-tooltip="hasLongItemTitle && itemTitle"
      class="tools-navbar__itemTitle"
    >
      {{ itemTitle }}
    </span>

    <slot name="editItemTitle"></slot>
  </div>
</template>

<style lang="scss" scoped>
.tools-navbar {
  &__title {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    font-size: $font-size-sm;
    padding: $spacer-1 $spacer-2;
    padding-left: 0;
    white-space: nowrap;
    color: $dark;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__link {
    text-decoration: none;
    color: $color-black;

    &:hover,
    &:focus {
      color: $btn-black-hover-color;
    }
  }

  &__itemTitle {
    display: block;
    max-width: 300px;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
